<template>
  <Sheet
    v-model="isOpen"
    title="Favourites"
    size="lg"
    :fullscreen="fullscreen"
    content-class="p-0 min-h-[50vh]">
    <!-- Title Strip -->
    <div v-if="title" class="p-4 pb-0">
      <div class="overflow-hidden rounded-lg border border-gray-700">
        <TitleStrip :title="title" :condensed="true" :dark="true" />
      </div>
      <!-- Quick remove for current title -->
      <button
        type="button"
        class="w-full px-4 py-2.5 mt-2 text-sm font-medium transition-colors rounded-lg"
        :class="
          currentTitleItem
            ? 'cursor-pointer text-white/40 bg-white/5 hover:bg-red-500/15 hover:text-red-400'
            : 'cursor-not-allowed text-white/15 bg-white/[0.02]'
        "
        :disabled="!currentTitleItem || removingCurrent"
        @click="removeCurrentTitle">
        <template v-if="removingCurrent">
          <fa-icon :icon="['fas', 'spinner-third']" spin />
        </template>
        <template v-else>Remove from Favourites</template>
      </button>
    </div>

    <div v-if="loading && items.length === 0" class="flex justify-center py-12">
      <fa-icon
        :icon="['fas', 'spinner-third']"
        spin
        class="text-xl text-white/40" />
    </div>

    <div v-else-if="items.length === 0" class="px-6 py-12 text-center">
      <fa-icon :icon="['far', 'heart']" class="mb-3 text-3xl text-white/30" />
      <p class="text-sm text-white/50">No favourites yet</p>
    </div>

    <template v-else>
      <!-- Item count -->
      <div
        class="flex items-center justify-between px-4 py-2 border-b border-white/10">
        <span class="text-xs text-white/50">
          {{ items.length }} {{ items.length === 1 ? 'title' : 'titles' }}
        </span>
      </div>

      <!-- Favourites list -->
      <div class="divide-y divide-white/10">
        <div
          v-for="item in items"
          :key="item.id"
          class="relative overflow-hidden">
          <!-- Delete zone behind -->
          <div
            class="absolute inset-y-0 right-0 flex items-center justify-end px-4 bg-red-500/90"
            :style="{
              width: Math.max(0, -(swipeOffsets[item.id] || 0)) + 'px'
            }">
            <fa-icon
              v-if="-(swipeOffsets[item.id] || 0) > 30"
              :icon="['fas', 'trash']"
              class="text-white text-sm" />
          </div>

          <!-- Swipeable content row -->
          <div
            class="relative flex items-center gap-3 px-4 py-2.5 bg-black transition-colors"
            :class="{
              'transition-transform duration-200': !isSwiping(item.id)
            }"
            :style="{
              transform: `translateX(${swipeOffsets[item.id] || 0}px)`
            }"
            @touchstart="onTouchStart($event, item)"
            @touchmove="onTouchMove($event, item)"
            @touchend="onTouchEnd($event, item)">
            <!-- Poster -->
            <div
              class="flex-shrink-0 w-10 h-14 rounded overflow-hidden bg-white/5">
              <img
                v-if="getPosterUrl(item)"
                :src="getPosterUrl(item)"
                :alt="getTitle(item)"
                class="w-full h-full object-cover" />
              <div
                v-else
                class="w-full h-full flex items-center justify-center">
                <fa-icon :icon="['far', 'film']" class="text-white/20" />
              </div>
            </div>

            <!-- Title info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1.5">
                <p class="text-sm font-medium text-white truncate">
                  {{ getTitle(item) }}
                </p>
                <span
                  v-if="isNewlyAdded(item)"
                  class="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold leading-none text-red-100 bg-red-500/80 rounded-full uppercase">
                  new
                </span>
              </div>
              <p class="text-xs text-white/50">
                {{ getYear(item) }}
                <span v-if="getType(item)" class="ml-1">
                  {{ getType(item) }}
                </span>
              </p>
            </div>

            <!-- Remove button / confirm state -->
            <button
              v-if="confirmingItemId !== item.id"
              type="button"
              class="flex-shrink-0 p-2 text-red-400 hover:text-red-300 transition-colors"
              @click="startRemove(item)">
              <fa-icon :icon="['fas', 'heart']" class="text-sm" />
            </button>

            <div v-else class="flex-shrink-0 flex items-center gap-1.5">
              <button
                type="button"
                class="px-2.5 py-1 text-xs font-medium text-white/60 bg-white/10 rounded-md hover:bg-white/15 transition-colors"
                @click="cancelRemove">
                Cancel
              </button>
              <button
                type="button"
                class="px-2.5 py-1 text-xs font-medium text-white bg-red-500/80 rounded-md hover:bg-red-500 transition-colors"
                :disabled="removing"
                @click="confirmRemove(item)">
                <template v-if="removing">
                  <fa-icon
                    :icon="['fas', 'spinner-third']"
                    spin
                    class="text-xs" />
                </template>
                <template v-else>Remove</template>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Sheet>
</template>

<script setup>
import { computed, ref, reactive, watch } from 'vue'
import { Sheet } from '@/components/Options'
import TitleStrip from '@/components/TitleStrip.vue'
import { useStoreLists } from '@/stores/storeLists'
import { useStoreTitlesUser } from '@/stores/storeTitlesUser'
import { useStoreAuth } from '@/stores/storeAuth'
import { useHapticFeedback } from '@/composables/useHapticFeedback'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: Object,
    default: null
  },
  fullscreen: {
    type: Boolean,
    default: false
  },
  newlyAddedTmdbId: {
    type: [Number, String, null],
    default: null
  }
})

const emit = defineEmits(['update:modelValue'])

const storeLists = useStoreLists()
const storeTitlesUser = useStoreTitlesUser()
const storeAuth = useStoreAuth()
const haptics = useHapticFeedback()

const loading = ref(false)
const confirmingItemId = ref(null)
const removing = ref(false)
const removingCurrent = ref(false)

// Swipe state
const swipeOffsets = reactive({})
const swipeState = reactive({})
const SWIPE_THRESHOLD = 80

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const userId = computed(() => storeAuth.user?.id)

const activeList = computed(() => {
  if (!userId.value) return null
  return storeLists.getSystemListByTypeAndUser('favourites', userId.value)
})

const items = computed(() => {
  if (!activeList.value?.id) return []
  return storeLists.getListItemsByListId(activeList.value.id)
})

// Find the list item matching the current title (for quick remove)
const currentTitleItem = computed(() => {
  if (!props.title?.id || !items.value.length) return null
  return items.value.find(
    (item) => item.titles?.tmdb_id == props.title.id
  )
})

// Load favourites when sheet opens
watch(isOpen, async (opened) => {
  if (opened && userId.value) {
    // Reset state
    confirmingItemId.value = null
    removing.value = false
    removingCurrent.value = false
    Object.keys(swipeOffsets).forEach((k) => delete swipeOffsets[k])
    Object.keys(swipeState).forEach((k) => delete swipeState[k])

    // Only show loading spinner if we have no data yet
    const hasData = activeList.value?.id && items.value.length > 0
    if (!hasData) {
      loading.value = true
    }

    try {
      await storeLists.fetchSystemListByTypeAndUser(
        'favourites',
        userId.value,
        true
      )
      if (activeList.value?.id) {
        await storeLists.fetchListItemsByListIdPaginated(activeList.value.id)
        await storeLists.autoLoadListItemsUpTo(activeList.value.id, 100, 20)
      }
    } finally {
      loading.value = false
    }
  }
})

// Helpers to extract data from list items
function getPosterUrl(item) {
  const cachePath = item.titles?.tmdb_cache?.poster_path
  const dbPath = item.titles?.tmdb_poster_path
  const path = cachePath || dbPath
  if (!path) return null
  return `https://image.tmdb.org/t/p/w185${path}`
}

function getTitle(item) {
  const cache = item.titles?.tmdb_cache
  return cache?.title || cache?.name || item.titles?.title || 'Unknown'
}

function getYear(item) {
  const cache = item.titles?.tmdb_cache
  const date =
    cache?.release_date ||
    cache?.first_air_date ||
    item.titles?.release_date ||
    item.titles?.first_air_date
  if (!date) return ''
  return date.substring(0, 4)
}

function getType(item) {
  const type = item.titles?.tmdb_type
  if (type === 'tv') return 'TV'
  if (type === 'movie') return 'Film'
  return ''
}

function isNewlyAdded(item) {
  if (!props.newlyAddedTmdbId) return false
  return item.titles?.tmdb_id == props.newlyAddedTmdbId
}

// Swipe gesture handlers
function isSwiping(itemId) {
  return swipeState[itemId]?.active
}

function onTouchStart(event, item) {
  // Don't start swipe if confirming
  if (confirmingItemId.value === item.id) return
  const touch = event.touches[0]
  swipeState[item.id] = {
    startX: touch.clientX,
    startY: touch.clientY,
    active: false,
    locked: false
  }
}

function onTouchMove(event, item) {
  const state = swipeState[item.id]
  if (!state || state.locked) return

  const touch = event.touches[0]
  const deltaX = touch.clientX - state.startX
  const deltaY = touch.clientY - state.startY

  // Determine direction lock on first significant move
  if (!state.active && Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) return

  if (!state.active) {
    // If vertical movement dominates, lock out swipe
    if (Math.abs(deltaY) > Math.abs(deltaX)) {
      state.locked = true
      return
    }
    state.active = true
  }

  // Only allow left swipe (negative deltaX), with resistance
  if (deltaX < 0) {
    const clamped = Math.max(deltaX, -SWIPE_THRESHOLD * 1.5)
    swipeOffsets[item.id] = clamped
  } else {
    swipeOffsets[item.id] = 0
  }
}

function onTouchEnd(event, item) {
  const state = swipeState[item.id]
  if (!state) return

  const offset = swipeOffsets[item.id] || 0

  if (-offset >= SWIPE_THRESHOLD) {
    // Swiped past threshold - trigger confirm
    haptics.light()
    swipeOffsets[item.id] = 0
    confirmingItemId.value = item.id
  } else {
    // Snap back
    swipeOffsets[item.id] = 0
  }

  if (swipeState[item.id]) {
    swipeState[item.id].active = false
  }
}

async function removeCurrentTitle() {
  const item = currentTitleItem.value
  if (!item?.titles?.tmdb_id || removingCurrent.value) return
  haptics.medium()
  removingCurrent.value = true
  try {
    // Remove from local store first for immediate UI update
    storeLists.removeItem(item.id)
    await storeTitlesUser.setFaved(false, {
      titleId: item.titles.tmdb_id,
      titleType: item.titles.tmdb_type || 'movie'
    })
    haptics.success()
  } catch (error) {
    haptics.error()
    console.error('Failed to remove favourite:', error)
  } finally {
    removingCurrent.value = false
  }
}

function startRemove(item) {
  haptics.light()
  confirmingItemId.value = item.id
}

function cancelRemove() {
  haptics.light()
  confirmingItemId.value = null
}

async function confirmRemove(item) {
  if (!item.titles?.tmdb_id || removing.value) return
  haptics.medium()
  removing.value = true
  try {
    // Remove from local store first for immediate UI update
    storeLists.removeItem(item.id)
    confirmingItemId.value = null
    await storeTitlesUser.setFaved(false, {
      titleId: item.titles.tmdb_id,
      titleType: item.titles.tmdb_type || 'movie'
    })
    haptics.success()
  } catch (error) {
    haptics.error()
    console.error('Failed to remove favourite:', error)
  } finally {
    removing.value = false
  }
}
</script>
